import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Crown, Shield, User as UserIcon, Instagram, Wrench, BookOpen, Menu, X, FolderOpen, FileText, Tag, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { loginWithGoogle } from "@/lib/api";
import { useFeatures, isFeatureVisible } from "@/lib/features";
import { InstallAppButton } from "@/components/InstallApp";
import { openChat } from "@/components/chat/ChatAlerts";

export default function Navbar({ minimal }) {
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useFeatures();
  const doLogout = async () => { setOpen(false); await logout(); navigate("/"); };
  const isPro = user && (user.role === "admin" || user.is_pro);
  const daysLeft = user?.pro_days_left || 0;
  const igOn = isFeatureVisible("instagram_post_maker");
  const proLabel = user?.role === "admin" ? "Admin" : (daysLeft > 0 ? `Pro: ${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "Pro");

  const links = [
    !minimal && user && { to: "/", label: "Projects", icon: FolderOpen, id: "projects" },
    !minimal && user && { to: "/tools", label: "Check out our tools", icon: Wrench, id: "tools" },
    !minimal && { to: "/pages", label: "Pages", icon: FileText, id: "pages" },
    !minimal && igOn && { to: "/instagram-post-maker", label: "Instagram Post Maker", icon: Instagram, id: "instagram-post-maker" },
    { to: "/pricing", label: "Pricing", icon: Tag, id: "pricing" },
    !minimal && { to: "/how-to-use", label: "How to Use", icon: BookOpen, id: "how-to-use" },
    user?.role === "admin" && { to: "/admin", label: "Admin", icon: Shield, id: "admin" },
  ].filter(Boolean);

  return (
    <nav className="sticky top-0 z-40 h-14 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex h-full max-w-[1600px] items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2.5" data-testid="nav-logo">
          <img src="/captionmotion-logo.png" alt="Caption Motion" className="h-9 w-9 shrink-0 rounded-lg object-contain" />
          <span className="truncate font-display text-lg font-black tracking-tight">Caption Motion</span>
        </Link>
        {/* desktop */}
        <div className="hidden items-center gap-5 text-sm font-medium text-[#A1A1AA] lg:flex">
          {links.map((l) => l.id === "instagram-post-maker" ? (
            <Link key={l.id} to={l.to} data-testid={`nav-${l.id}`} className="flex items-center gap-1.5 rounded-full border border-[#E1306C]/50 bg-gradient-to-r from-[#E1306C]/15 to-[#F77737]/15 px-3 py-1.5 text-xs font-bold text-[#f48fb1] transition-colors hover:text-white"><Instagram size={14} /> Try Instagram Post Maker</Link>
          ) : (
            <Link key={l.id} to={l.to} data-testid={`nav-${l.id}`} className="flex items-center gap-1.5 transition-colors hover:text-white">{["tools", "how-to-use", "admin"].includes(l.id) && <l.icon size={14} />}{l.label}</Link>
          ))}
          {user ? (
            <div className="flex items-center gap-3">
              {isPro ? (
                <Link to="/profile" data-testid="nav-pro-days" className="flex items-center gap-1.5 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-3 py-1.5 text-xs font-bold text-[#00F0FF]"><Crown size={13} /> {proLabel}</Link>
              ) : (
                <Link to="/pricing" data-testid="nav-upgrade" className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black transition-transform hover:scale-[1.03]"><Crown size={13} /> Get Pro</Link>
              )}
              <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2">
                <Avatar user={user} />
                <span className="flex items-center gap-1 text-sm text-white">{user.name?.split(" ")[0]}{isPro && <Crown size={12} className="text-[#FFD400]" />}</span>
              </Link>
              <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-white/30"><LogOut size={14} /> Logout</button>
            </div>
          ) : (
            <button onClick={loginWithGoogle} data-testid="login-btn" className="rounded-full bg-white px-4 py-1.5 text-sm font-bold text-black transition-colors hover:bg-white/90">Login</button>
          )}
        </div>
        {/* mobile / tablet */}
        <div className="flex items-center gap-2 lg:hidden">
          {user ? <Link to="/profile" data-testid="nav-profile-mobile"><Avatar user={user} /></Link>
            : <button onClick={loginWithGoogle} data-testid="login-btn-mobile" className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-black">Login</button>}
          <button onClick={() => setOpen(true)} data-testid="nav-menu-btn" aria-label="Open menu" className="rounded-lg p-2 text-white hover:bg-white/10"><Menu size={22} /></button>
        </div>
      </div>
      {open && createPortal(<MobileMenu links={links} user={user} isPro={isPro} proLabel={proLabel} onClose={() => setOpen(false)} onLogout={doLogout} />, document.body)}
    </nav>
  );
}

const Avatar = ({ user }) => user.picture
  ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
  : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#00F0FF]"><UserIcon size={15} className="text-black" /></div>;

function MobileMenu({ links, user, isPro, proLabel, onClose, onLogout }) {
  return (
    <div className="fixed inset-0 z-[60] lg:hidden" data-testid="nav-mobile-menu">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-[82%] max-w-[340px] animate-in slide-in-from-right flex-col border-l border-white/10 bg-[#0D0D0E] p-5 duration-200">
        <div className="mb-6 flex items-center justify-between">
          <span className="font-display text-lg font-black">Menu</span>
          <button onClick={onClose} data-testid="nav-menu-close" aria-label="Close menu" className="rounded-lg p-2 text-white hover:bg-white/10"><X size={22} /></button>
        </div>
        {user && (
          <Link to="/profile" onClick={onClose} className="mb-4 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="nav-mobile-profile">
            <Avatar user={user} />
            <div className="min-w-0"><div className="truncate text-sm font-bold text-white">{user.name || user.email}</div>
              <div className="text-xs text-[#00F0FF]">{isPro ? proLabel : "Free plan"}</div></div>
          </Link>
        )}
        <div className="flex-1 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <Link key={l.id} to={l.to} onClick={onClose} data-testid={`nav-mobile-${l.id}`} className="flex items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-semibold text-white/85 hover:bg-white/5">
              <l.icon size={18} className="text-[#A1A1AA]" /> {l.label}
            </Link>
          ))}
          {user && user.role !== "admin" && (
            <button onClick={() => { onClose(); openChat(); }} data-testid="nav-mobile-chat" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-semibold text-white/85 hover:bg-white/5"><MessageCircle size={18} className="text-[#00a884]" /> Chat with us</button>
          )}
          <InstallAppButton onDone={onClose} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-semibold text-white/85 hover:bg-white/5" />
          {user && !isPro && (
            <Link to="/pricing" onClick={onClose} data-testid="nav-mobile-upgrade" className="mt-2 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] py-3 text-sm font-bold text-black"><Crown size={15} /> Get Pro</Link>
          )}
        </div>
        {user ? (
          <button onClick={onLogout} data-testid="nav-mobile-logout" className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-sm font-semibold text-white"><LogOut size={16} /> Logout</button>
        ) : (
          <button onClick={loginWithGoogle} data-testid="nav-mobile-login" className="mt-4 rounded-xl bg-white py-3 text-sm font-bold text-black">Login</button>
        )}
      </div>
    </div>
  );
}
